import { isNumericText } from './markdown.ts';

/**
 * What every table in the console shares: how a column decides its alignment, and the classes
 * of its header, rows, cells and footer (components.md, Tables; typography.md, Numbers).
 * DataTable, VirtualTable and the Table primitives use these, and a hand-built table or a
 * grid that acts as one uses `tableClass` so it reads the same.
 */
export type CellAlign = 'left' | 'right';

/** What a column holds. Numbers, money, durations and percentages align right in `num`. */
export type ColumnType = 'text' | 'badge' | 'date' | 'number' | 'money' | 'duration' | 'percent';

export const NUMERIC_TYPES: readonly ColumnType[] = ['number', 'money', 'duration', 'percent'];

/** The part of a column the alignment reads. */
export interface AlignSource<T> {
  align?: CellAlign;
  /** Declares the column numeric (true) or not (false); wins over the type and the data. */
  numeric?: boolean;
  type?: ColumnType;
  sortValue?: (row: T) => string | number | null | undefined;
  cell?: (row: T) => unknown;
}

/** How many rows the inference reads: enough to be sure, cheap on a long list. */
const SAMPLE = 50;

/**
 * Whether sampled values are one column of numbers: every present value a number, or (for
 * rendered text) at least 60% of the present values read as one. Missing values do not count.
 */
export function inferNumeric(values: readonly unknown[]): boolean {
  let present = 0;
  let numeric = 0;
  for (const v of values) {
    if (v == null || v === '' || typeof v === 'boolean') continue;
    present++;
    if (typeof v === 'number' || typeof v === 'bigint') numeric++;
    else if (typeof v === 'string' && isNumericText(v)) numeric++;
    else return false;
  }
  return present > 0 && numeric / present >= 0.6;
}

/**
 * The column's alignment. In order: an explicit `align`, then `numeric`, then the declared
 * `type`, then the data: a column whose sort value is a number on every row, or (with no sort
 * value) whose cell renders as plain numbers, aligns right. A sort value that is a rank (a
 * status order, a boolean as 0 and 1) needs `type: 'badge'` or `numeric: false`.
 */
export function columnAlign<T>(c: AlignSource<T>, rows: readonly T[]): CellAlign {
  if (c.align) return c.align;
  if (c.numeric != null) return c.numeric ? 'right' : 'left';
  if (c.type) return NUMERIC_TYPES.includes(c.type) ? 'right' : 'left';
  const sample = rows.length > SAMPLE ? rows.slice(0, SAMPLE) : rows;
  if (c.sortValue) {
    const sortValue = c.sortValue;
    const values = sample.map((r) => sortValue(r));
    // Sort values are data, not text: numbers only.
    return values.some((v) => v != null) && values.every((v) => v == null || typeof v === 'number') ? 'right' : 'left';
  }
  if (c.cell) {
    const cell = c.cell;
    const values = sample.map((r) => cell(r));
    if (values.every((v) => v == null || typeof v === 'string' || typeof v === 'number')) return inferNumeric(values) ? 'right' : 'left';
  }
  return 'left';
}

/** The name column: the one that grows, else the first. It reads bright; the rest are muted. */
export function nameColumn(columns: readonly { key: string; grow?: boolean }[]): string | undefined {
  return (columns.find((c) => c.grow) ?? columns[0])?.key;
}

/** Numbers, newest dates and longest durations first; names and labels A to Z first. */
export function sortsDescFirst<T>(c: AlignSource<T>, align: CellAlign): boolean {
  return c.type === 'date' || align === 'right';
}

/**
 * The classes every table shares. Headers small, dim and 500; rows at row height with a thin
 * divider and a hover tint; the name column bright, the rest muted; numbers right in `num`; a
 * dim footer for totals.
 */
export const tableClass = {
  /** A header cell (or a grid's column header). */
  head: 'h-row whitespace-nowrap align-middle text-caption font-medium text-fg-subtle',
  /** The header row: the divider under it, no hover. */
  headRow: 'h-row border-b border-border-subtle',
  /** A body row. */
  row: 'h-row border-b border-border-subtle last:border-b-0 transition-colors duration-(--duration-fast) ease-standard hover:bg-hover',
  /** A row that can be selected: the same row without the hover, which the caller adds when it is not selected. */
  rowBase: 'h-row border-b border-border-subtle last:border-b-0 transition-colors duration-(--duration-fast) ease-standard',
  /** The hover tint of a row that is not selected. */
  rowHover: 'hover:bg-hover',
  /** The one selected look: a soft accent fill with a quiet edge, never a strip at the left. */
  selected: 'selected-tint',
  /** A body cell. */
  cell: 'align-middle text-ui text-fg-muted',
  /** The identity cell: the row's name. */
  name: 'text-ui font-medium text-fg',
  /** A right-aligned number. */
  numeric: 'text-right num',
  /** The footer row (totals): dim, under a divider. */
  footRow: 'h-row border-t border-border-subtle text-ui text-fg-subtle',
} as const;
